import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ReduxState, RentalLinkData } from 'services/types';
import {
	Languages,
	OrderInfo,
	ResponsiblePerson,
	OrderProduct,
	PhoneObject,
	ShopPublicInfo,
} from 'common/types';
import { StaticContext } from 'react-router';
import { RouteComponentProps, Redirect, withRouter } from 'react-router-dom';
import {
	setHandlePayment,
	setRentalLinkInfo,
	updateResponsiblePerson,
	sendRentalLink,
	updatePhone,
	setPendingRentalLinks,
	removeReservation,
} from 'actions';
import { Theme, SelectChangeEvent, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import TextField from '@mui/material/TextField';
import Container from 'components/Container';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Email';
import CommentIcon from '@mui/icons-material/InsertComment';
import Grid from '@mui/material/Grid';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import TestPaymentAlert from 'components/TestPaymentAlert';
import { getLanguageNativeName } from 'common/locales/utils';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import countryCodes from 'common/services/countryCodes.json';
import InputAdornment from '@mui/material/InputAdornment';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import Spinner from 'common/components/Spinner';

import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageTitle from 'components/Portals/SetPageTitle';

import { Routes } from 'routing';
import { getRentalLinkUrl } from 'services/utils/common';
import { useTranslation } from 'services/localization/useTranslation';
import { isCustomShopPaymentMethodObject } from 'common/modules/payments/paymentMethods';
import { getTranslation } from 'common/modules/translations';

interface DispatchProps {
	sendRentalLink: (lang: string, rentalLinkId: string) => Promise<boolean>;
	setRentalLinkInfo: (field: string, value: string | null) => void;
	setHandlePayment: (payment: boolean) => void;
	updateResponsiblePerson: (responsible: ResponsiblePerson) => void;
	updatePhone: (phone: PhoneObject) => void;
	setPendingRentalLinks: (rentalLinks: RentalLinkData[]) => void;
	removeReservation: () => void;
}

interface StateToProps {
	rentalInfo: OrderInfo;
	lang: Languages;
	products: OrderProduct[];
	phone: PhoneObject;
	pendingLinks: RentalLinkData[];
	shopInfo: ShopPublicInfo;
}

type LocationState = {
	from: { [key: string]: string };
};

type Props = DispatchProps &
	StateToProps &
	WithStyles<typeof styles> &
	RouteComponentProps<{}, StaticContext, LocationState>;

const RentalLinkInfo = (props: Props) => {
	const [rentalLinkLanguage, setRentalLinkLanguage] = useState<string>(props.lang);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');

	const { classes, rentalInfo, phone } = props;
	const pendingRentalLinks = props.pendingLinks;
	const { from } = (props.location && props.location.state) || {
		from: { pathname: Routes.NEW_RENTAL },
	};
	const selectedAreaCode = phone.countryCode + ':' + phone.areaCode;
	const { t } = useTranslation();

	useEffect(() => {
		return () => {
			props.removeReservation();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sendRentalLink = async () => {
		const { rentalInfo } = props;
		if (!rentalInfo.rentalLink) {
			return;
		}
		setLoading(true);

		const lang = rentalLinkLanguage;
		const rentalLinkId = newFirestoreId();
		if (rentalInfo.rentalLink.phone || rentalInfo.rentalLink.email) {
			const rentalLinkUrl = getRentalLinkUrl(rentalLinkId, lang);
			const newPendingRentalLink: RentalLinkData = {
				shopPublicInfo: props.shopInfo,
				startLocation: rentalInfo.startLocation,
				email: rentalInfo.rentalLink.email || '',
				phone: props.phone,
				payLinkUrl: rentalLinkUrl,
				language: lang,
				rentalId: rentalInfo.id,
				status: '',
			};
			props.setPendingRentalLinks([...pendingRentalLinks, newPendingRentalLink]);
		}
		const sent = await props.sendRentalLink(lang, rentalLinkId);
		props.removeReservation();
		setLoading(false);
		setError(sent ? '' : t('common:errors.error'));
	};

	const handleRentalLinkInfoChange = (field: string, value: string | null) => {
		props.setRentalLinkInfo(field, value);
	};

	const handleRentalLinkPhoneNumberChange = (
		e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const value = e.target.value;
		const phone = props.phone.areaCode + value;
		props.setRentalLinkInfo('phone', value !== '' ? phone : null);
		props.updatePhone({
			...props.phone,
			numberWithoutCode: value,
		});
	};

	const setLanguage = (event: SelectChangeEvent) => {
		const language = event.target.value;
		if (typeof language === 'string') {
			setRentalLinkLanguage(language);
		}
	};

	const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const [countryCode, areaCode] = e.target.value.split(':');
		props.updatePhone({
			...props.phone,
			areaCode,
			countryCode,
		});
		if (props.phone.numberWithoutCode) {
			const phone = areaCode + props.phone.numberWithoutCode;
			props.setRentalLinkInfo('phone', phone);
		}
	};

	const getPaymentMethodName = () => {
		const { paymentMethod } = rentalInfo.pricing;
		if (!!paymentMethod && isCustomShopPaymentMethodObject(paymentMethod)) {
			return getTranslation(paymentMethod.details.label, props.lang);
		}
		return t('payLinkInfo.cashPayment', 'Pay in store on pick-up');
	};

	if (!rentalInfo.id || !props.products.length) {
		return <Redirect to={from} />;
	}
	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{
					to: Routes.NEW_RENTAL,
					title: t('newRental.editRental', 'Edit rental'),
					onClick: () => props.removeReservation(),
				}}
			/>
			<SetPageTitle priority={2} title={t('newRental.customerDetails', 'Customer details')} />
			<div className={classes.root}>
				{rentalInfo.handlePayment && <TestPaymentAlert />}
				<Container className={classes.container}>
					<FormGroup className={classes.formGroup}>
						<Grid container spacing={1} className={classes.inputWrapper} alignItems="center">
							<Grid item>
								<MailIcon
									color={
										rentalInfo.rentalLink && rentalInfo.rentalLink.email != null
											? 'primary'
											: 'action'
									}
								/>
							</Grid>
							<Grid item xs={true}>
								<TextField
									data-intercom-target="Email input"
									autoFocus
									id="email"
									name="email"
									label={t('newRental.paymentLinkByEmail', 'Send by email')}
									className={classes.textField}
									value={(rentalInfo.rentalLink && rentalInfo.rentalLink.email) || ''}
									onChange={(e) => handleRentalLinkInfoChange('email', e.target.value || null)}
									type="email"
									variant="outlined"
								/>
							</Grid>
						</Grid>
						<Grid container spacing={1} className={classes.inputWrapper} alignItems="center">
							<Grid item>
								<PhoneIcon
									color={
										rentalInfo.rentalLink && rentalInfo.rentalLink.phone != null
											? 'primary'
											: 'action'
									}
								/>
							</Grid>
							<Grid item xs={true}>
								<TextField
									data-intercom-target="Phone number input"
									id="phone"
									name="phone"
									label={t('newRental.paymentLinkBySMS', 'Send by SMS')}
									className={classes.textField}
									variant="outlined"
									value={phone.numberWithoutCode}
									onChange={handleRentalLinkPhoneNumberChange}
									type="tel"
									autoComplete="off"
									InputProps={{
										classes: { root: classes.noPaddingLeft },
										startAdornment: (
											<InputAdornment position="start" classes={{ root: classes.areaCodeRoot }}>
												<TextField
													select
													value={selectedAreaCode}
													onChange={handleAreaCodeChange}
													SelectProps={{
														native: true,
														classes: { select: classes.select },
													}}
													InputProps={{
														disableUnderline: true,
													}}
													variant="standard"
												>
													{countryCodes.map((countryCode) => (
														<option
															key={countryCode.code}
															value={countryCode.code + ':' + countryCode.dial_code}
														>
															{selectedAreaCode === countryCode.code + ':' + countryCode.dial_code
																? `${countryCode.flag} ${countryCode.dial_code}`
																: `${countryCode.flag} ${countryCode.name} (${countryCode.dial_code})`}
														</option>
													))}
												</TextField>
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>
						<div className={classes.additionalInfoWrapper}>
							<Typography
								variant="body1"
								color="textSecondary"
								gutterBottom
								align="left"
								className={classes.additionalInfo}
							>
								{t('payLinkInfo.inAdditionInformation', 'In addition you can add:')}
							</Typography>
							<Grid container spacing={1} className={classes.inputWrapper} alignItems="center">
								<Grid item>
									<PersonIcon
										color={
											rentalInfo.rentalLink &&
											(rentalInfo.rentalLink.firstName != null ||
												rentalInfo.rentalLink.lastName != null)
												? 'primary'
												: 'action'
										}
									/>
								</Grid>
								<Grid item container xs={true} spacing={1}>
									<Grid item xs={6}>
										<TextField
											id="firstNam"
											name="firstName"
											label={t('common:form.firstName')}
											className={classes.textField}
											value={(rentalInfo.rentalLink && rentalInfo.rentalLink.firstName) || ''}
											onChange={(e) =>
												handleRentalLinkInfoChange('firstName', e.target.value || null)
											}
											type="email"
											variant="outlined"
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											id="lastName"
											name="lastName"
											label={t('common:form.lastName')}
											className={classes.textField}
											value={(rentalInfo.rentalLink && rentalInfo.rentalLink.lastName) || ''}
											onChange={(e) =>
												handleRentalLinkInfoChange('lastName', e.target.value || null)
											}
											type="email"
											variant="outlined"
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid container spacing={1} alignItems="flex-start" className={classes.inputWrapper}>
								<Grid item className={classes.textAreaIconFix}>
									<CommentIcon
										color={
											rentalInfo.rentalLink && rentalInfo.rentalLink.comment ? 'primary' : 'action'
										}
									/>
								</Grid>
								<Grid item xs={true}>
									<TextField
										label={t('payLinkInfo.comment', 'Additional information for the shopper')}
										multiline
										rows={3}
										maxRows={5}
										value={
											rentalInfo.rentalLink && rentalInfo.rentalLink.comment
												? rentalInfo.rentalLink.comment
												: ''
										}
										onChange={(e) => handleRentalLinkInfoChange('comment', e.target.value)}
										className={classes.textField}
										variant="outlined"
									/>
								</Grid>
							</Grid>
						</div>
						<Box px={4} py={3} display="flex" flexDirection="row" flexWrap="wrap">
							<FormControl sx={{ mr: 3, mb: 2 }} variant="standard">
								<InputLabel htmlFor="language" variant="standard">
									{t('payLinkInfo.language', 'Language')}
								</InputLabel>
								<Select
									id="language"
									variant="standard"
									value={rentalLinkLanguage}
									onChange={setLanguage}
									inputProps={{
										name: 'language',
										id: 'language-selector',
									}}
									autoWidth={true}
									disableUnderline={true}
								>
									<MenuItem value={'fi'}>{getLanguageNativeName('fi')}</MenuItem>
									<MenuItem value={'en'}>{getLanguageNativeName('en')}</MenuItem>
								</Select>
							</FormControl>
							<FormControl sx={{ mr: 3, mb: 2 }} variant="standard">
								<Typography
									variant="body1"
									color="textSecondary"
									fontSize={12}
									fontWeight={500}
									lineHeight="17px"
									sx={{ mb: '2px' }}
								>
									{t('payLinkInfo.paymentMethod', 'Payment method')}
								</Typography>

								<Typography>{getPaymentMethodName()}</Typography>
							</FormControl>
						</Box>

						{error ? <Typography color="error">{error}</Typography> : null}
						<div className={classes.btnBlockWrapper}>
							<Button
								data-intercom-target="Send rental link button"
								variant="contained"
								color="primary"
								size="large"
								className={classes.btn}
								data-qa="send"
								disabled={
									loading ||
									!rentalInfo.rentalLink ||
									(!rentalInfo.rentalLink.email && !rentalInfo.rentalLink.phone) ||
									(rentalInfo.rentalLink.phone != null && !rentalInfo.rentalLink.phone) ||
									(rentalInfo.rentalLink.email != null && !rentalInfo.rentalLink.email) ||
									loading
								}
								onClick={() => sendRentalLink()}
							>
								{loading ? <Spinner padding={8} thickness={4} /> : t('common:actions.send', 'Send')}
							</Button>
						</div>
					</FormGroup>
				</Container>
			</div>
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
			flex: '1 1 auto',
		},
		container: {
			paddingTop: 16,
		},
		textField: {
			width: '100%',
		},
		inputWrapper: {
			marginTop: 8,
			marginBottom: 8,
		},
		languageSelectorWrapper: {
			paddingLeft: 24,
		},
		btnBlockWrapper: {
			display: 'block',
			textAlign: 'left',
			marginLeft: theme.spacing(4),
		},
		btn: {
			height: 40,
		},
		formGroup: {
			display: 'block',
			flexWrap: 'nowrap',
		},
		formControlLabel: {
			display: 'flex',
		},
		formControl: {
			margin: theme.spacing(1),
		},
		rentalInfo: {
			marginTop: 32,
		},
		textAreaIconFix: {
			marginTop: 16,
		},
		additionalInfoWrapper: {
			marginTop: theme.spacing(2),
		},
		additionalInfo: {
			paddingLeft: 0,
		},
		areaCodeRoot: {
			borderRight: '1px solid rgba(0, 0, 0, 0.23)',
			padding: '28px 0',
			paddingLeft: 14,
			flex: '0 0 110px',
		},
		noPaddingLeft: {
			paddingLeft: 0,
		},
		select: {
			width: 'calc(100% - 24px)',
			paddingRight: 24,
		},
		spinner: {
			padding: theme.spacing(1),
		},
	});

const mapStateToProps = ({ newRental, lang, view, shop }: ReduxState): StateToProps => {
	const shopInfo = shop.shop.data!.publicInfo;
	return {
		rentalInfo: newRental.rentalInfo,
		lang,
		products: newRental.products,
		phone: newRental.phone,
		pendingLinks: view.pendingRentalLinks,
		shopInfo,
	};
};

const dispatchProps: DispatchProps = {
	sendRentalLink: sendRentalLink as any,
	setRentalLinkInfo,
	setHandlePayment,
	updateResponsiblePerson,
	updatePhone,
	setPendingRentalLinks,
	removeReservation,
};

export default withStyles(styles)(
	withRouter(connect(mapStateToProps, dispatchProps)(RentalLinkInfo)),
);
